import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Grid} from '@material-ui/core';
import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import IconWithTooltip from '../Tooltip/IconWithTooltip';
import TextField from '../TextField/TextField';
import DialogContainer, {DialogButton} from '../AlertDialog/DialogContainer';
import classNames from 'classnames';
import {MenuActionFormType} from '../Forms/AutoAttendantsMenus/ActionsForm.utils';
import {AutoAttendantListItem, AutoAttendantMenu, MenuAction, UserInput,} from '../../store/types/AutoAttendant';
import CustomizedCheckbox from '../Checkbox/Checkbox';
import FileSelect from '../FileSelect/FileSelect';
import {useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import {CallQueue} from '../../store/types/CallQueue';
import DialingPad from './utils/DialingPad';
import {MultiLevelSelectField, MultiLevelSelectItem,} from '../MultiLevelSelectField/MultiLevelSelectField';
import {Delete} from '@material-ui/icons';
import CustomizedButton from '../Button/Button';
import {usePermissionContext} from '../../hooks/usePermissions';
import {PermissionType} from '../../store/types/Permission';
import CustomizedTooltip from '../Tooltip/Tooltip';
import {useParams} from 'react-router';
import {ServiceFeatureName} from '../../store/types/ServiceFeature';
import {VoiceFileContentType} from "../../store/types/Voicemail";
import {prepareOggFile} from "../UploadFile/UploadFile";
import {
    availableListForDialExtensionDirectly,
    faxActions,
    initialValuesMenuActionForm,
    MenuActionDialogProps,
    MenuActionValidationSchema,
    useStyles
} from "./MenuActionDialog.utils";
import PlayFileButton from '../PlayFileButton/PlayFileButton';
import {menuTransitionPlaybackKey} from '../../utils/playbackIdentity';
import {useDispatch} from 'react-redux';
import { actions } from '../../store';

const MenuActionDialog: React.VFC<MenuActionDialogProps> = (
    {
        isOpen,
        toggleVisibility,
        menuActions,
        initialValue,
        onSave,
        isDialExtensionDirectlyBlocked,
        deleteAction,
    }) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const destinationRef = useRef<HTMLInputElement>(null);
    const {
        values,
        handleSubmit,
        handleChange,
        resetForm,
        setFieldValue,
        setFieldError,
        errors,
        dirty
    } = useFormik<MenuActionFormType>({
        initialValues: initialValue || initialValuesMenuActionForm,
        onSubmit: (form) => {
            onSave?.(form);
        },
        validationSchema: MenuActionValidationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        enableReinitialize: true,
    });

    useEffect(() => {
        isOpen && resetForm()
    }, [isOpen]);

    const voicemailSettings = useSelector((state: ReduxState) => state.extensions?.voicemailSettings);
    const voiceMaileSettingsValue = voicemailSettings?.ext_email !== undefined ? voicemailSettings?.ext_email : '';

    const serviceFeatures = useSelector(
        (state: ReduxState) => state.autoAttendants.autoAttendantDetails?.extension?.serviceFeatures
            || state.autoAttendants?.autoAttendantInfo?.account_info?.service_features
            || state.autoAttendants?.autoAttendantInfo?.serviceFeatures
            || state.extensions?.serviceFeatures
    );

    const unifiedMessagingVoicemailEnabled = useMemo(() => serviceFeatures
                ?.find((v) => v.name === ServiceFeatureName.UnifiedMessaging)
                ?.attributes.find((v) => v.name === 'fax_only_mode')?.effective_values[0]
            === "N",
        [serviceFeatures]);

    const unifiedMessagingVoicemailDisabledGlobal = useMemo(() => serviceFeatures
            ?.find((v) => v.name === ServiceFeatureName.UnifiedMessaging)?.effective_flag_value == 'N',
        [serviceFeatures]);

    const permission = usePermissionContext();

    const menuActionsList: MultiLevelSelectItem[] = useMemo(
        () =>
            Object.values(MenuAction).map((v) => ({
                label: unifiedMessagingVoicemailEnabled && v === MenuAction.TransferToFaxMailbox && values.userInput !== UserInput.Fax
                    ? t('enums:inactiveMenuAction.VoicemailEnabled')
                    : v === MenuAction.TransferToFaxMailbox
                        ? t('enums:inactiveMenuAction.Voicemail') + (" " + voiceMaileSettingsValue)
                        : t(`enums:inactiveMenuAction.${v}`),
                value: v,
                nestedItems: [],
                disabled: v == MenuAction.TransferToFaxMailbox && unifiedMessagingVoicemailDisabledGlobal

            })),
        [unifiedMessagingVoicemailEnabled, voicemailSettings, values?.userInput],
    );

    const menuFaxAction: MultiLevelSelectItem[] = useMemo(
        () =>
            faxActions.map((v) => ({
                label: unifiedMessagingVoicemailEnabled && v === MenuAction.TransferToFaxMailbox && values.userInput !== UserInput.Fax
                    ? t('enums:inactiveMenuAction.VoicemailEnabled')
                    : v === MenuAction.TransferToFaxMailbox
                        ? t('enums:inactiveMenuAction.Voicemail') + (" " + voiceMaileSettingsValue)
                        : t(`enums:inactiveMenuAction.${v}`),
                value: v,
                nestedItems: [],
                disabled: false
            })),
        [faxActions, unifiedMessagingVoicemailEnabled, voicemailSettings, values?.userInput],
    );

    const menus = useSelector<ReduxState, AutoAttendantMenu[]>(
        (state) => state.autoAttendants.autoAttendantMenus || [],
    );

    const callQueues = useSelector<ReduxState, CallQueue[]>(
        (state) => state.callQueues?.callQueues || [],
    );

    const DISAStatus = useSelector<ReduxState, boolean>(
        (state) => state.autoAttendants.incomingTab?.enableDisa === 'Y',
    );

    const saveDisabled = useMemo(() => {
        return !values.action || !values.userInput || permission === PermissionType.ReadOnly || !dirty
    }, [values.action, values.userInput, permission, dirty]);

    const autoAttendantList = useSelector<ReduxState, AutoAttendantListItem[] | undefined>(
        (state) => state.autoAttendants?.autoAttendantList?.items
    );

    const account_info = useSelector(
        (state: ReduxState) => state.autoAttendants.autoAttendantDetails?.extension?.account_info
            ?? state.autoAttendants?.autoAttendantInfo?.account_info
    );

    // @ts-ignore
    const id = useParams()?.id || '';
    const attendantMenusFoundInState = useMemo(() => {
        return autoAttendantList?.filter((e: AutoAttendantListItem) => e.extension_id == id);
    }, [autoAttendantList]);

    const onChangeUserInput = useCallback(
        (value: UserInput) => {
            setFieldValue('userInput', value);

            if (
                value === UserInput.Fax &&
                values.action &&
                !faxActions.includes(values.action)
            ) {
                setFieldValue('action', MenuAction.DoNothing);
            }
        },
        [values.action],
    );

    const selectedLabel = () => {
        if (values.action === MenuAction.Queue) {
            const queue = callQueues.find(
                (v) => v.i_c_queue === values.queue?.i_c_queue,
            );

            const label = `${t(`enums:inactiveMenuAction.${MenuAction.Queue}`)} / ${
                queue?.group_name
            }`

            return queue?.group_name ? label : label.replace('/ undefined', '')

        } else if (values.action === MenuAction.Menu) {
            const menu = menus.find((v) => v.i_menu === values.menu?.i_menu);
            return `${t(`enums:inactiveMenuAction.${MenuAction.Menu}`)} / ${
                menu?.name
            }`;
        } else if (values.action === MenuAction.TransferToFaxMailbox) {
            return unifiedMessagingVoicemailEnabled && values.userInput !== UserInput.Fax
                ? t('enums:inactiveMenuAction.VoicemailEnabled')
                : t('enums:inactiveMenuAction.Voicemail') + (" " + voiceMaileSettingsValue);
        } else {
            return t(`enums:inactiveMenuAction.${values.action}`);
        }
    };

    const items: MultiLevelSelectItem[] = useMemo(() => {
        const items = [...menuActionsList];

        const menuIndex = items.findIndex((v) => v.value === MenuAction.Menu);

        items[menuIndex].nestedItems = menus.map((v) => ({
            value: v.i_menu,
            label: v.name,
        }));

        items[menuIndex].disabled = !menus.length;

        const queueIndex = items.findIndex((v) => v.value === MenuAction.Queue);

        items[queueIndex].nestedItems = callQueues.map((v) => ({
            value: v.i_c_queue || 0,
            label: v.group_name || '',
        }));

        if (!callQueues.length) {
            items[queueIndex].disabled = true;
        }

        if (
            !!isDialExtensionDirectlyBlocked ||
            (values.userInput !== undefined &&
                !availableListForDialExtensionDirectly.includes(
                    values.userInput,
                ))
        ) {
            const dialExtensionIndex = items.findIndex(
                (v) => v.value === MenuAction.DialExtensionDirectly,
            );

            items[dialExtensionIndex].disabled = true;
        }

        if (!DISAStatus) {
            const disaIndex = items.findIndex(
                (v) => v.value === MenuAction.DISA,
            );
            items[disaIndex].disabled = true;
        }

        return items;
    }, [
        menuActionsList,
        DISAStatus,
        isDialExtensionDirectlyBlocked,
        values.userInput,
        availableListForDialExtensionDirectly,
        menus,
        callQueues,
        attendantMenusFoundInState,
    ]);

    const onChangeAction = useCallback(
        (parentValue: string | number, childValue?: string | number) => {

            if (parentValue === MenuAction.Queue) {
                const queue = callQueues.find(
                    (v) => v.i_c_queue === childValue,
                );
                setFieldValue('queue', queue);
            } else if (parentValue === MenuAction.Menu) {
                const menu = menus.find((v) => v.i_menu === childValue);
                setFieldValue('menu', menu);
            }
            setFieldValue('action', parentValue);
        },
        [setFieldValue, menus, callQueues],
    );

    useEffect(() => {
        if (destinationRef) {
            destinationRef?.current?.focus();
        }
    }, [values.action]);

    const renderPlayFileButton = useCallback((menuId: number, transitionId: number) => {
        return (
            <PlayFileButton
                files={[{
                    call_recording_id: 'auto_attendant_menu_transition',
                    playbackKey: menuTransitionPlaybackKey(transitionId),
                    h323_conf_id: transitionId + '',
                    customDownloadFileAction: (callback, restrictedCallback) => {
                        dispatch(
                            actions.getMenuTransitionPromptFileAsBlob.request({
                                codec: 'wav',
                                i_menu: menuId,
                                i_menu_transition: transitionId,
                                callback: callback,
                                restrictedCallback: restrictedCallback,
                                accountId: account_info?.id,
                                domain: account_info?.um_domain,
                            }),
                        );
                    }
                }]}
                customClasses={{
                    beforePlaying: classes.playPauseButtonLoading,
                    initial: classes.playPauseButtonNotStarted
                }}
                dataQa={"play-file-icon" + menuId + "_" + transitionId}
                dataTestId={"play-file-icon-id" + menuId + "_" + transitionId}
                customPlayText={t("common:play")}
                customStopText={t("common:stop")}
                stopPlayingInsteadOfPause
            />
        );
    }, []);

    return (
        <DialogContainer
            isOpen={isOpen}
            dataQa="sip-dialog"
            header={
                permission === PermissionType.ReadOnly ?
                    t('screens:autoAttendants.menuActionDetails')
                    : (initialValue
                        ? t('screens:autoAttendants.editActionMenu')
                        : t('screens:autoAttendants.addNewAction'))
            }
            headerClass={classes.header}
            className={classes.modalContainer}
            dialogLeftSideActionsButtons={[
                initialValue ? (
                    <div key={'delete-left'}>
                        <CustomizedButton
                            dataQa="add-new-menu-button"
                            onClick={() =>
                                deleteAction && deleteAction(initialValue)
                            }
                            ternary
                        >
                            <Delete/>
                            {t('screens:autoAttendants.deleteAction')}
                        </CustomizedButton>
                    </div>
                ) : null,
            ]}
            dialogActionsButtons={[
                <div key="right">
                    <DialogButton
                        label={t('common:cancel')}
                        onClick={() => {
                            toggleVisibility?.();
                        }}
                    />
                    <DialogButton
                        label={t('common:save')}
                        onClick={handleSubmit}
                        disabled={saveDisabled}
                        primary
                        className={classes.saveButton}
                    />
                </div>,
            ]}
        >
            <form
                onSubmit={handleSubmit}
                autoComplete="off"
                data-testid="action-form"
            >
                <DialingPad
                    id="userInput"
                    value={values.userInput || null}
                    setFieldError={setFieldError}
                    onChange={onChangeUserInput}
                    menuActions={menuActions}
                    isDialExtensionDirectly={
                        values.action === MenuAction.DialExtensionDirectly
                    }
                />

                <Grid item className={classes.itemsContainer}>
                    {values.userInput === UserInput.NoInput && (
                        <CustomizedTooltip
                            title={t('common:noPermissionToResource')}
                            copy={false}
                            disableHoverListener={permission === PermissionType.Visible}
                        >
                            <Grid
                                className={classNames(
                                    classes.centeredGrid,
                                    classes.numberPickerGrid,
                                )}
                            >
                                <TextField
                                    id="timeout"
                                    label={t('screens:extensions.timeout')}
                                    onChange={(event) => {
                                        const value = event.target.value === '' ? 0 : event.target.value;

                                        setFieldValue('timeout', value);
                                        setFieldError('timeout', undefined);
                                    }}
                                    value={values.timeout}
                                    type="number"
                                    iconPosition="end"
                                    className={classes.numberInput}
                                    dataQa="timeout-input"
                                    inputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 999,
                                        },
                                        pattern: '[0-9]*',
                                    }}
                                    helperText={errors.timeout}
                                />

                                <IconWithTooltip
                                    dataQa="timeout-tooltip"
                                    tooltipText={t(
                                        'tooltips:autoAttendants.timeout',
                                    )}
                                />
                            </Grid>
                        </CustomizedTooltip>
                    )}

                    <CustomizedTooltip
                        title={t('common:noPermissionToResource')}
                        copy={false}
                        disableHoverListener={permission === PermissionType.Visible}
                    >
                        <div style={{width: '100%', height: 'fit-content'}}>
                            <MultiLevelSelectField
                                label={t('common:action')}
                                items={
                                    values.userInput === UserInput.Fax
                                        ? menuFaxAction
                                        : items
                                }
                                value={selectedLabel()}
                                onChange={onChangeAction}
                                readOnly={permission === PermissionType.ReadOnly}
                                required
                            />
                        </div>
                    </CustomizedTooltip>

                    {values.action === MenuAction.Transfer && (
                        <CustomizedTooltip
                            title={t('common:noPermissionToResource')}
                            copy={false}
                            disableHoverListener={permission === PermissionType.Visible}
                        >
                            <div style={{width: '100%', height: 'fit-content'}}>
                                <TextField
                                    label={t(
                                        'screens:autoAttendants.transferCallerToPhoneNumberExtension',
                                    )}
                                    id="transferCallerToPhoneNumberExtension"
                                    setFieldError={setFieldError}
                                    value={values.transferCallerToPhoneNumberExtension}
                                    onChange={handleChange}
                                    required
                                    className={classes.selectMargin}
                                    maxLength={32}
                                    inputRef={destinationRef}
                                    helperText={
                                        errors.transferCallerToPhoneNumberExtension
                                    }
                                />
                            </div>
                        </CustomizedTooltip>
                    )}

                    {values.action === MenuAction.TransferToE164Number && (
                        <CustomizedTooltip
                            title={t('common:noPermissionToResource')}
                            copy={false}
                            disableHoverListener={permission === PermissionType.Visible}
                        >
                            <div style={{width: '100%', height: 'fit-content'}}>
                                <TextField
                                    label={t(
                                        'screens:autoAttendants.transferCallerToPhoneNumberExtension',
                                    )}
                                    id="transferCallerToPhoneNumberExtension"
                                    setFieldError={setFieldError}
                                    value={values.transferCallerToPhoneNumberExtension}
                                    onChange={handleChange}
                                    required
                                    className={classes.selectMargin}
                                    inputProps={{
                                        pattern: '[0-9]*',
                                    }}
                                    maxLength={15}
                                    helperText={
                                        errors.transferCallerToPhoneNumberExtension
                                    }
                                    inputRef={destinationRef}
                                />
                            </div>
                        </CustomizedTooltip>
                    )}

                    {(values.action === MenuAction.PromptForExtension ||
                        values.action === MenuAction.DialExtensionDirectly) && (
                        <Grid
                            className={classNames(
                                classes.centeredGrid,
                                classes.numberPickerGrid,
                            )}
                        >
                            <TextField
                                id="maxDigits"
                                label={t('screens:autoAttendants.maxDigits')}
                                onChange={handleChange}
                                value={values.maxDigits}
                                type="number"
                                iconPosition="end"
                                className={classes.numberInput}
                                dataQa="max-digits-input"
                                inputProps={{
                                    inputProps: {
                                        min: 1,
                                        max: 32,
                                    },
                                    pattern: '[0-9]*',
                                }}
                                defaultEmptyValue={'1'}
                            />

                            <IconWithTooltip
                                dataQa="max-digits-tooltip"
                                tooltipText={t(
                                    'tooltips:autoAttendants.maxDigits',
                                )}
                            />
                        </Grid>
                    )}

                    {values.action === MenuAction.DialByNameDirectory && (
                        <>
                            <Grid
                                className={classNames(
                                    classes.centeredGrid,
                                    classes.playBeforeGrid,
                                )}
                            >
                                <CustomizedTooltip
                                    title={t('common:noPermissionToResource')}
                                    copy={false}
                                    disableHoverListener={permission === PermissionType.Visible}
                                >
                                    <div style={{width: 'fit-content', height: 'fit-content'}}>
                                        <CustomizedCheckbox
                                            checked={values.playBeforeActionStatus}
                                            dataQa="play-before-action-status"
                                            onChange={(value) =>
                                                setFieldValue(
                                                    'playBeforeActionStatus',
                                                    value.target.checked,
                                                )
                                            }
                                            label={t(
                                                'screens:autoAttendants.directoryIntroPrompt',
                                            )}
                                        />
                                    </div>
                                </CustomizedTooltip>

                                <IconWithTooltip
                                    dataQa="play-before-action-tooltip"
                                    tooltipText={t(
                                        'tooltips:autoAttendants.playBeforeAction',
                                    )}
                                />
                            </Grid>

                            {values.playBeforeActionStatus && (
                                <div className={classes.fileWrapper}>
                                    <FileSelect
                                        nameValue={
                                            values.playBeforeActionFileName
                                        }
                                        onFileChange={(file) => {

                                            let audioFile;
                                            // @ts-ignore
                                            if (file && [VoiceFileContentType.VideoOgg, VoiceFileContentType.AppOgg].includes(file?.type)) {
                                                audioFile = prepareOggFile(file)
                                            }

                                            setFieldValue(
                                                'playBeforeActionFileName',
                                                audioFile ? audioFile.name : file.name);
                                            setFieldValue(
                                                'playBeforeActionFile',
                                                audioFile ?? file);
                                        }}
                                        file={values.playBeforeActionFile}
                                        customStyles={{
                                            textField: classes.buttonFileTextField,
                                            buttonStyle: classes.buttonFile
                                        }}
                                        customIcon={renderPlayFileButton(values.menu?.i_menu ?? 0, values.transitionId ?? 0)}
                                    />
                                </div>
                            )}

                            <CustomizedTooltip
                                title={t('common:noPermissionToResource')}
                                copy={false}
                                disableHoverListener={permission === PermissionType.Visible}
                            >
                                <div style={{width: 'fit-content', height: 'fit-content'}}>
                                    <CustomizedCheckbox
                                        checked={values.announceExtensionNumbers}
                                        dataQa="play-before-action-status"
                                        onChange={(value) =>
                                            setFieldValue(
                                                'announceExtensionNumbers',
                                                value.target.checked,
                                            )
                                        }
                                        label={t(
                                            'screens:autoAttendants.announceExtensionNumbers',
                                        )}
                                    />
                                </div>
                            </CustomizedTooltip>
                        </>
                    )}
                    {values.action !== MenuAction.DialByNameDirectory && (
                        <>
                            <Grid
                                className={classNames(
                                    classes.centeredGrid,
                                    classes.playBeforeGrid,
                                )}
                            >
                                <CustomizedTooltip
                                    title={t('common:noPermissionToResource')}
                                    copy={false}
                                    disableHoverListener={permission === PermissionType.Visible}
                                >
                                    <div style={{width: 'fit-content', height: 'fit-content'}}>
                                        <CustomizedCheckbox
                                            checked={values.playBeforeActionStatus}
                                            dataQa="play-before-action-status"
                                            onChange={(value) =>
                                                setFieldValue(
                                                    'playBeforeActionStatus',
                                                    value.target.checked,
                                                )
                                            }
                                            label={t(
                                                'screens:autoAttendants.playBeforeAction',
                                            )}
                                        />
                                    </div>
                                </CustomizedTooltip>

                                <IconWithTooltip
                                    dataQa="play-before-action-tooltip"
                                    tooltipText={t(
                                        'tooltips:autoAttendants.playBeforeAction',
                                    )}
                                />
                            </Grid>

                            {values.playBeforeActionStatus && (
                                <div className={classes.fileWrapper}>
                                    <FileSelect
                                        nameValue={
                                            values.playBeforeActionFileName
                                        }
                                        onFileChange={(file) => {

                                            let audioFile;
                                            // @ts-ignore
                                            if (file && [VoiceFileContentType.VideoOgg, VoiceFileContentType.AppOgg].includes(file?.type)) {
                                                audioFile = prepareOggFile(file)
                                            }

                                            setFieldValue(
                                                'playBeforeActionFileName',
                                                audioFile ? audioFile.name : file.name);
                                            setFieldValue(
                                                'playBeforeActionFile',
                                                audioFile ?? file);
                                        }}
                                        file={values.playBeforeActionFile}
                                        customStyles={{
                                            textField: classes.buttonFileTextField,
                                            buttonStyle: classes.buttonFile
                                        }}
                                        customIcon={renderPlayFileButton(values.menu?.i_menu ?? 0, values.transitionId ?? 0)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Grid>
            </form>
        </DialogContainer>
    );
};

export default MenuActionDialog;
